import { isEmpty, sum, sumBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useInventoryEventLocalContext } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/InventoryEventLocalContext';
import { InventoryEventTabs } from 'src/components/Listings/InventoryEventPage/InventoryEventTabs';
import { useAppContext } from 'src/contexts/AppContext';
import { useMobileInventorySortAndFilterContext } from 'src/contexts/MobileSortAndFilterContext/MobileInventorySortAndFilterContext';
import { useComputedState } from 'src/hooks/useComputedState';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  getAllInPriceFromListPrice,
  getStubHubSellerFee,
} from 'src/utils/inventoryUtils';
import { getMarketplaceSettings } from 'src/utils/userUtils';
import {
  CompListing,
  IListingGroupItem,
  Listing,
  Marketplace,
  SectionInfo,
} from 'src/WebApiController';

import {
  InventoryMetrics,
  ListingTileItem,
  ListingWithComp,
} from './ListingTilesV2.types';
import {
  filterListingBySection,
  groupCompListingsBetweenOwnListings,
  isPriceRangeGroup,
  mapCompListingToListing,
} from './ListingTilesV2.utils';

export const useInventoryListings = (
  ownListings: Listing[],
  compListings: CompListing[],
  selectedSections: SectionInfo[]
) => {
  const { activeTab } = useInventoryEventLocalContext();

  const [selectedSectionIds, selectedSectionNames] = useMemo(() => {
    const distinctNames = new Set<string>();
    const distinctIds = new Set<number>();
    for (const section of selectedSections) {
      distinctNames.add(section.name);
      distinctIds.add(section.id);
    }
    return [distinctIds, distinctNames];
  }, [selectedSections]);

  const { unsortedInventory, ownListingsMapped, allListingsMapped } =
    useMergedInventory(
      ownListings,
      compListings,
      true,
      activeTab === InventoryEventTabs.ManualPricing
    );

  const sortAndFilteredAllInventory = useMemo(() => {
    if (isEmpty(selectedSectionIds) && isEmpty(selectedSectionNames)) {
      return allListingsMapped;
    }
    return allListingsMapped.filter(
      ({ seating }) =>
        (seating.sectionId && selectedSectionIds.has(seating.sectionId)) ||
        (seating.section && selectedSectionNames.has(seating.section))
    );
  }, [allListingsMapped, selectedSectionIds, selectedSectionNames]);

  const sortAndFilteredOwnInventory = useMemo(() => {
    if (isEmpty(selectedSectionIds) && isEmpty(selectedSectionNames)) {
      return ownListingsMapped;
    }
    return (
      ownListingsMapped.filter(
        filterListingBySection(selectedSectionIds, selectedSectionNames)
      ) ?? []
    );
  }, [ownListingsMapped, selectedSectionIds, selectedSectionNames]);

  const inventoryMetrics = useMemo<InventoryMetrics>(() => {
    const listingCount = sortAndFilteredOwnInventory.length;
    const ticketCount = sumBy(
      sortAndFilteredOwnInventory,
      (l) => l.availQty ?? 0
    );
    const allAvailable = sumBy(
      sortAndFilteredAllInventory,
      (l) => l.availQty ?? 0
    );
    return {
      listingCount,
      ticketCount,
      allAvailableTickets:
        activeTab === InventoryEventTabs.Info ? undefined : allAvailable, // Hide all availables for my listings
    };
  }, [activeTab, sortAndFilteredAllInventory, sortAndFilteredOwnInventory]);

  return {
    ownInventory: sortAndFilteredOwnInventory,
    allInventory: unsortedInventory,
    allInventoryWithGroups: groupCompListingsBetweenOwnListings(
      sortAndFilteredAllInventory
    ),
    inventoryMetrics,
  };
};

export const useMergedInventory = (
  ownListings: Listing[],
  compListings: CompListing[],
  withSorting = false,
  allInventory = false // Flag to tell we are on My Inventory or All Inventory
) => {
  const { loginContext } = useAppContext();
  const { withListingSortingCriteria } =
    useMobileInventorySortAndFilterContext();
  const ownListingsMapped = useMemo<ListingWithComp[]>(() => {
    return ownListings
      ?.flatMap(flattenListingGroup)
      .filter((l) => l.isFull)
      .map((lgi: IListingGroupItem) => {
        const listing = lgi as Listing;
        // Need to recalculate all in price to match display value
        const stubHubMkpSettings = getMarketplaceSettings(
          loginContext?.user?.activeAccount,
          Marketplace.StubHub,
          listing.officeId
        );
        const allInPrice = getAllInPriceFromListPrice(
          listing.listPrice,
          listing,
          getStubHubSellerFee(listing, stubHubMkpSettings?.sellerFee)
        );

        return {
          ...listing,
          allInPrice,
          isCompListing: false,
        } as ListingWithComp;
      });
  }, [ownListings, loginContext?.user?.activeAccount]);

  const ownListingsStubHubListingIds = useMemo(
    () =>
      ownListingsMapped
        .filter((l) =>
          l.mkpListings?.some((ml) => ml.mkp === Marketplace.StubHub)
        )
        .map(
          (l) =>
            l.mkpListings?.find((ml) => ml.mkp === Marketplace.StubHub)
              ?.mkpListingId ?? ''
        ),
    [ownListingsMapped]
  );

  const compListingsMapped = useMemo<ListingWithComp[]>(
    () =>
      compListings
        .map(mapCompListingToListing)
        ?.map((l) => ({ ...l, isCompListing: true }) as ListingWithComp)
        .filter(
          (cl) => !ownListingsStubHubListingIds.includes(cl.id.toString())
        ) ?? [],
    [compListings, ownListingsStubHubListingIds]
  );

  const ownListingsWithSort = useMemo(() => {
    if (withSorting) {
      return withListingSortingCriteria(ownListings, allInventory);
    }
    return ownListings.toSorted(
      (l1, l2) =>
        (l1.allInPrice ?? l1.listPrice) - (l2.allInPrice ?? l1.listPrice)
    );
  }, [allInventory, ownListings, withSorting, withListingSortingCriteria]);

  const allListingsMappedWithSort = useMemo(() => {
    const allListings = [...ownListingsMapped, ...compListingsMapped];
    if (withSorting) {
      return withListingSortingCriteria(allListings, allInventory);
    }
    return allListings.toSorted(
      (l1, l2) => (l1.allInPrice ?? 0) - (l2.allInPrice ?? 0)
    );
  }, [
    allInventory,
    compListingsMapped,
    ownListingsMapped,
    withSorting,
    withListingSortingCriteria,
  ]);

  return {
    unsortedInventory: [...ownListingsMapped, ...compListingsMapped],
    ownListingsMapped: ownListingsWithSort,
    allListingsMapped: allListingsMappedWithSort,
  };
};

export const useGroupedVirtuoso = (inventory: ListingTileItem[]) => {
  const initialGroupCount = useMemo(
    () =>
      inventory.reduce(
        (acc, inv, index) => {
          if (isPriceRangeGroup(inv)) {
            acc[index] = inv.groupItems.length;
          } else {
            acc[index] = 0;
          }
          return acc;
        },
        {} as Record<number, number>
      ),
    [inventory]
  );

  const defaultCount = useMemo(
    () => new Array<number>(inventory.length).fill(0),
    [inventory.length]
  );

  const [localGroupCounts, setLocalGroupCounts] =
    useComputedState(defaultCount);

  const getGroupItemIndexFromItemIndex = useCallback(
    (itemIndex: number, groupIndex: number): number => {
      const prevGroups = localGroupCounts.slice(0, groupIndex);
      const prevSum = sum(prevGroups);
      return itemIndex - prevSum;
    },
    [localGroupCounts]
  );

  const onUpdateExpanded = useCallback(
    (groupIndex: number) => (expanded: boolean) => {
      setLocalGroupCounts((prev) => {
        const cloned = [...prev];
        const count = expanded ? initialGroupCount[groupIndex] : 0;
        cloned[groupIndex] = count;
        return cloned;
      });
    },
    [initialGroupCount, setLocalGroupCounts]
  );

  return {
    localGroupCounts,
    getGroupItemIndexFromItemIndex,
    onUpdateExpanded,
  };
};
